import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Dialog,
    Box,
    InputBase,
    List,
    ListSubheader,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Typography,
    // CircularProgress,
    Stack,
    Chip,
    Divider,
} from '@mui/material';
import {
    Search as SearchIcon,
    ShoppingCart,
    People,
    Inventory,
    ContentCut,
    Dashboard as DashboardIcon,
    Settings as SettingsIcon,
    Webhook,
    NotificationsActive,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// import { getOrders } from '../../api/apiOrders';
// import { getCustomers } from '../../api/apiCustomers';
// import { getProducts } from '../../api/apiProducts';
// import { getCoupons } from '../../api/apiCoupons';

// Static destinations always shown (and matchable by label).
const STATIC_DESTINATIONS = [
    { key: 'dashboard', path: '/dashboard', icon: DashboardIcon, en: 'Dashboard', ar: 'الرئيسية' },
    { key: 'orders', path: '/orders', icon: ShoppingCart, en: 'Orders', ar: 'الطلبات' },
    { key: 'customers', path: '/customers', icon: People, en: 'Customers', ar: 'العملاء' },
    { key: 'products', path: '/products', icon: Inventory, en: 'Products', ar: 'المنتجات' },
    { key: 'inventory', path: '/inventory', icon: Inventory, en: 'Inventory', ar: 'المخزون' },
    { key: 'coupons', path: '/coupons', icon: ContentCut, en: 'Coupons', ar: 'الكوبونات' },
    { key: 'settings', path: '/settings/general', icon: SettingsIcon, en: 'Settings — General', ar: 'الإعدادات — عامة' },
    { key: 'notifs', path: '/settings/notifications', icon: NotificationsActive, en: 'Notification templates', ar: 'قوالب الإشعارات' },
    { key: 'webhooks', path: '/settings/integrations/webhooks', icon: Webhook, en: 'Webhooks', ar: 'Webhooks' },
];

// const DEBOUNCE_MS = 220;

// Run all four list endpoints in parallel and shape the result for
// the palette's grouped UI. All errors are swallowed individually
// so a single 5xx doesn't blank the whole panel.
// const fetchFederated = async (q) => {
//     const safe = q.trim();
//     if (!safe) return { orders: [], customers: [], products: [], coupons: [] };
//     const small = { search: safe, per_page: 5 };

//     // const [o, c, p, k] = await Promise.allSettled([
//     //     getOrders(small),
//     //     getCustomers(small),
//     //     getProducts(small),
//     //     getCoupons(small),
//     // ]);

//     return {
//         orders:    o.status === 'fulfilled' ? (o.value?.data || []) : [],
//         customers: c.status === 'fulfilled' ? (c.value?.data || []) : [],
//         products:  p.status === 'fulfilled' ? (p.value?.data || []) : [],
//         coupons:   k.status === 'fulfilled' ? (k.value?.data || []) : [],
//     };
// };

const CommandPalette = ({ open, onClose }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const navigate = useNavigate();

    const [query, setQuery] = useState('');
    // const [debounced, setDebounced] = useState('');
    const [results, setResults] = useState({ orders: [], customers: [], products: [], coupons: [] });
    // const [isFetching, setIsFetching] = useState(false);

    const inputRef = useRef(null);
    // const reqIdRef = useRef(0);

    useEffect(() => {
        if (open) {
            setQuery('');
            // setDebounced('');
            setResults({ orders: [], customers: [], products: [], coupons: [] });
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [open]);

    // useEffect(() => {
    //     const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    //     return () => clearTimeout(id);
    // }, [query]);

    // useEffect(() => {
    //     if (!open) return;
    //     const q = debounced.trim();
    //     if (!q) {
    //         setResults({ orders: [], customers: [], products: [], coupons: [] });
    //         return;
    //     }
    //     const reqId = ++reqIdRef.current;
    //     setIsFetching(true);
    //     fetchFederated(q)
    //         .then((data) => {
    //             if (reqId === reqIdRef.current) setResults(data);
    //         })
    //         .finally(() => {
    //             if (reqId === reqIdRef.current) setIsFetching(false);
    //         });
    // }, [debounced, open]);

    const filteredStatic = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return STATIC_DESTINATIONS;
        return STATIC_DESTINATIONS.filter((d) =>
            (d.en + ' ' + d.ar).toLowerCase().includes(q)
        );
    }, [query]);

    const go = (path) => {
        navigate(path);
        onClose?.();
    };

    // const hasAny =
    //     filteredStatic.length > 0
    //     || results.orders.length > 0
    //     || results.customers.length > 0
    //     || results.products.length > 0
    //     || results.coupons.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: { borderRadius: 3, mt: 8, alignSelf: 'flex-start' },
            }}
        >
            <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25 }}>
                <SearchIcon sx={{ color: 'text.secondary', me: 1 }} />
                <InputBase
                    inputRef={inputRef}
                    fullWidth
                    placeholder={isArabic
                        ? 'ابحث عن طلب، عميل، منتج، كوبون...'
                        : 'Search orders, customers, products, coupons…'}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{ fontSize: 16, mx: 1 }}
                />
                {/* {isFetching && <CircularProgress size={18} sx={{ ms: 1 }} />} */}
                <Chip
                    size="small"
                    label="Esc"
                    variant="outlined"
                    sx={{ ml: 1, fontFamily: 'monospace' }}
                />
            </Stack>

            <Divider />

            <Box sx={{ maxHeight: 480, overflowY: 'auto', minHeight: 80 }}>
                {/* {!hasAny && !isFetching && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {isArabic ? 'لا توجد نتائج' : 'No results'}
                        </Typography>
                    </Box>
                )} */}

                <List dense>
                    {filteredStatic.length > 0 && (
                        <>
                            <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 700 }}>
                                {isArabic ? 'انتقل إلى' : 'Go to'}
                            </ListSubheader>
                            {filteredStatic.map((d) => {
                                const Icon = d.icon;
                                return (
                                    <ListItemButton key={d.key} onClick={() => go(d.path)}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <Icon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={isArabic ? d.ar : d.en} />
                                    </ListItemButton>
                                );
                            })}
                        </>
                    )}

                    {results.orders.length > 0 && (
                        <>
                            <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 700 }}>
                                {isArabic ? 'الطلبات' : 'Orders'}
                            </ListSubheader>
                            {results.orders.map((o) => (
                                <ListItemButton key={`o-${o.id}`} onClick={() => go(`/orders/${o.id}`)}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <ShoppingCart fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`#${o.number || o.id}`}
                                        secondary={o.customer?.name || o.customer?.email || ''}
                                    />
                                    <Chip
                                        size="small"
                                        label={o.status}
                                        sx={{ fontFamily: 'monospace', fontSize: 10 }}
                                    />
                                </ListItemButton>
                            ))}
                        </>
                    )}

                    {results.customers.length > 0 && (
                        <>
                            <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 700 }}>
                                {isArabic ? 'العملاء' : 'Customers'}
                            </ListSubheader>
                            {results.customers.map((c) => (
                                <ListItemButton key={`c-${c.id}`} onClick={() => go(`/customers/${c.id}`)}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <People fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={c.name || c.email}
                                        secondary={c.email || c.phone || ''}
                                    />
                                </ListItemButton>
                            ))}
                        </>
                    )}

                    {results.products.length > 0 && (
                        <>
                            <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 700 }}>
                                {isArabic ? 'المنتجات' : 'Products'}
                            </ListSubheader>
                            {results.products.map((p) => (
                                <ListItemButton key={`p-${p.id}`} onClick={() => go(`/products/${p.id}/edit`)}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Inventory fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={p.name?.[isArabic ? 'ar' : 'en'] || p.name?.en || p.name || `#${p.id}`}
                                        secondary={p.sku || ''}
                                    />
                                </ListItemButton>
                            ))}
                        </>
                    )}

                    {results.coupons.length > 0 && (
                        <>
                            <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 700 }}>
                                {isArabic ? 'الكوبونات' : 'Coupons'}
                            </ListSubheader>
                            {results.coupons.map((k) => (
                                <ListItemButton key={`k-${k.id}`} onClick={() => go(`/coupons/${k.id}/edit`)}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <ContentCut fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={k.code}
                                        secondary={k.effective_status || ''}
                                    />
                                </ListItemButton>
                            ))}
                        </>
                    )}
                </List>
            </Box>

            <Divider />
            <Stack
                direction="row"
                spacing={1}
                sx={{ px: 2, py: 1, color: 'text.secondary', fontSize: 12 }}
                alignItems="center"
            >
                <Chip size="small" label="↑ ↓" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                <Typography variant="caption">{isArabic ? 'تنقل' : 'Navigate'}</Typography>
                <Chip size="small" label="↵" variant="outlined" sx={{ fontFamily: 'monospace', ml: 1 }} />
                <Typography variant="caption">{isArabic ? 'فتح' : 'Open'}</Typography>
            </Stack>
        </Dialog>
    );
};

export default CommandPalette;
